import { AgxRow } from '@urbanx/agx.ui-components2';
import {
  CampaignDetailModelState,
  CoolingOffDetails,
  FormCondition,
  Condition,
  LinkedDateImpact,
  LinkedDateImpactSourceDateType,
  StaffPreviewLinkedDatesRequest,
  StaffPreviewLinkedDatesRequestSourceDateType,
  StaffUpdateContractDatesRequest,
  useInvalidateUseStaffGetContractDates,
  useStaffPreviewLinkedDates,
  useStaffGetContractDates,
  useStaffUpdateContractDates,
  WaiverOfCoolingOff,
} from '@urbanx/agx.campaigns.client';
import { Pages } from 'constants/menu';
import { useSelectedCampaign, useSelectedContract } from 'hooks';
import { isDirtyEqual } from 'utils/compares';
import { ConditionPanel } from './panels/ConditionPanel';
import { AddCoolingOffPeriod } from './modals/AddCoolingOffPeriod';
import { CoolingOffPeriodPanel } from './panels/CoolingOffPeriodPanel';
import { AddSubjectToFinance } from './modals/AddSubjectToFinance';
import { AddBuildingAndPest } from './modals/AddBuildingAndPest';
import { AddBodyCorporateRecords } from './modals/AddBodyCorporateRecords';
import { ContractDatePanel } from './panels/ContractDatePanel';
import { WaiverOfCoolingOffPanel } from './panels/WaiverOfCoolingOffPanel';
import { AddWaiverOfCoolingOff } from './modals/AddWaiverOfCoolingOff';
import { LinkedDateWarningModal } from './modals/LinkedDateWarningModal';
import { useGetSpecialConditionsInfo } from 'hooks/useGetSpecialConditionsInfo';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import useRexDisconnectedModal, {
  RexDisconnectedModalChange,
} from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import { dateOnlyToDate, toDateOnlyString } from '@urbanx/agx.ui-components2';
import { ContractInformationPage } from './ContractInformationPage';
import { useContractInformationEditor } from './useContractInformationEditor';

const mapImpactSourceDateType = (sourceDateType: LinkedDateImpactSourceDateType | undefined) => {
  switch (sourceDateType) {
    case LinkedDateImpactSourceDateType.FinanceDate:
      return StaffPreviewLinkedDatesRequestSourceDateType.FinanceDate;
    case LinkedDateImpactSourceDateType.BuildingAndPestDate:
      return StaffPreviewLinkedDatesRequestSourceDateType.BuildingAndPestDate;
    case LinkedDateImpactSourceDateType.SpecialConditionDate:
      return StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate;
    case LinkedDateImpactSourceDateType.ContractConditionChanges:
      return StaffPreviewLinkedDatesRequestSourceDateType.ContractConditionChanges;
    case LinkedDateImpactSourceDateType.ContractDate:
    default:
      return StaffPreviewLinkedDatesRequestSourceDateType.ContractDate;
  }
};

const getLinkedDateImpactSourceDateTypes = (impactedDates: LinkedDateImpact[]) => [
  ...new Set(impactedDates.map((impact) => mapImpactSourceDateType(impact.sourceDateType))),
];

const ContractDates = () => {
  const { campaign } = useSelectedCampaign();
  const { contract, isDocuSigned } = useSelectedContract();
  const invalidateUseStaffGetContractDates = useInvalidateUseStaffGetContractDates();

  const {
    data: contractDates,
    isPending,
    isError,
  } = useStaffGetContractDates(
    campaign?.campaignId || '',
    contract?.contractId || '',
    !!campaign?.campaignId && !!contract?.contractId
  );

  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  const { staffUpdateContractDates } = useStaffUpdateContractDates({
    onSuccess: () => {},
    onError: () => {},
  });

  const { staffPreviewLinkedDates } = useStaffPreviewLinkedDates({
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaign?.campaignId || '',
    campaign?.campaignId !== undefined
  );

  type ContractDatesDraft = {
    contractDate: string | undefined;
    coolingOffDetails: CoolingOffDetails | undefined;
    waiverOfCoolingOff: WaiverOfCoolingOff | undefined;
    subjectToFinance: FormCondition | undefined;
    buildingAndPestCondition: FormCondition | undefined;
    bodyCorporateRecordsCondition: FormCondition | undefined;
  };

  type LinkedDateConfirmation = {
    impactedDates: LinkedDateImpact[];
    sourceDateTypes: StaffPreviewLinkedDatesRequestSourceDateType[];
  };

  const source: ContractDatesDraft | undefined = contractDates
    ? {
        contractDate: contractDates.contractDate,
        coolingOffDetails: contractDates.coolingOffDetails,
        waiverOfCoolingOff: contractDates.waiverOfCoolingOff,
        subjectToFinance: contractDates.subjectToFinance,
        buildingAndPestCondition: contractDates.buildingAndPestCondition,
        bodyCorporateRecordsCondition: contractDates.bodyCorporateRecordsCondition,
      }
    : undefined;

  const conditionsChanged = (
    baseline: Readonly<ContractDatesDraft>,
    draft: Readonly<ContractDatesDraft>
  ) =>
    !isDirtyEqual(baseline.coolingOffDetails, draft.coolingOffDetails) ||
    !isDirtyEqual(baseline.waiverOfCoolingOff, draft.waiverOfCoolingOff) ||
    !isDirtyEqual(baseline.subjectToFinance, draft.subjectToFinance) ||
    !isDirtyEqual(baseline.buildingAndPestCondition, draft.buildingAndPestCondition) ||
    !isDirtyEqual(baseline.bodyCorporateRecordsCondition, draft.bodyCorporateRecordsCondition);

  const editor = useContractInformationEditor<ContractDatesDraft, LinkedDateConfirmation>({
    resourceKey: `${campaign?.campaignId ?? ''}:${contract?.contractId ?? ''}:conditions`,
    source,
    isPending,
    isError,
    preflight: async ({ baseline, draft }) => {
      if (campaign?.state !== CampaignDetailModelState.QLD) return undefined;

      const contractDateChanged = baseline.contractDate !== draft.contractDate;
      const contractConditionsChanged = conditionsChanged(baseline, draft);
      const sourceDateType = contractDateChanged
        ? StaffPreviewLinkedDatesRequestSourceDateType.ContractDate
        : contractConditionsChanged
          ? StaffPreviewLinkedDatesRequestSourceDateType.ContractConditionChanges
          : undefined;

      if (!sourceDateType) return undefined;

      const request: StaffPreviewLinkedDatesRequest = {
        sourceDateType,
        ...(sourceDateType === StaffPreviewLinkedDatesRequestSourceDateType.ContractDate && {
          proposedDate: draft.contractDate,
        }),
        coolingOffDetails: draft.coolingOffDetails,
        waiverOfCoolingOff: draft.waiverOfCoolingOff,
        subjectToFinance: draft.subjectToFinance,
        buildingAndPestCondition: draft.buildingAndPestCondition,
        bodyCorporateRecordsCondition: draft.bodyCorporateRecordsCondition,
      };
      const preview = await staffPreviewLinkedDates({
        campaignId: campaign.campaignId!,
        contractId: contract!.contractId,
        body: request,
      });
      const impactedDates = preview?.impactedDates ?? [];

      return impactedDates.length > 0
        ? {
            impactedDates,
            sourceDateTypes: getLinkedDateImpactSourceDateTypes(impactedDates),
          }
        : undefined;
    },
    persist: async ({ baseline, draft }) => {
      const request: StaffUpdateContractDatesRequest = {
        contractDate: draft.contractDate,
        coolingOffDetails: draft.coolingOffDetails,
        waiverOfCoolingOff: draft.waiverOfCoolingOff,
        subjectToFinance: draft.subjectToFinance,
        buildingAndPestCondition: draft.buildingAndPestCondition,
        bodyCorporateRecordsCondition: draft.bodyCorporateRecordsCondition,
      };
      const response = await staffUpdateContractDates({
        body: request,
        contractId: contract!.contractId,
      });

      if (!response?.success) return { accepted: false, errors: response?.errors ?? [] };

      if (!rexListing && isPendingRexListing === false) {
        const changes: RexDisconnectedModalChange[] = [];

        if (baseline.contractDate !== draft.contractDate) {
          changes.push({
            field: RexDisconnectedModalFields.ContractDate,
            value: draft.contractDate
              ? dateOnlyToDate(draft.contractDate)?.toLocaleDateString()
              : '',
          });
        }
        if (conditionsChanged(baseline, draft)) {
          changes.push({
            field: RexDisconnectedModalFields.ExpectedUnconditionalDate,
            value: '',
          });
        }

        openWithChanges(changes);
      }

      return { accepted: true };
    },
    refresh: {
      authority: 'source',
      refreshSource: () =>
        invalidateUseStaffGetContractDates(campaign!.campaignId!, contract!.contractId),
    },
  });

  const { specialConditionIdToClauseNumberMap } = useGetSpecialConditionsInfo(
    campaign?.campaignId,
    contract?.contractId
  );
  const showNonNswAddConditionButtons = campaign?.state !== CampaignDetailModelState.NSW;

  if (!editor.draft) {
    return (
      <ContractInformationPage currentPage={Pages.CONDITIONS} editor={editor}>
        {null}
      </ContractInformationPage>
    );
  }

  const {
    contractDate: contractDateString,
    coolingOffDetails,
    waiverOfCoolingOff,
    subjectToFinance,
    buildingAndPestCondition,
    bodyCorporateRecordsCondition,
  } = editor.draft;
  const contractDate = dateOnlyToDate(contractDateString) ?? undefined;

  return (
    <ContractInformationPage currentPage={Pages.CONDITIONS} editor={editor}>
      <ContractDatePanel
        id="contractDate"
        key={`ContractDatePanel${isPending ? 'Loaded' : ''}`}
        panelTitle="Contract Date"
        contractDate={contractDate}
        onChange={(value) =>
          editor.update('contractDate', value ? toDateOnlyString(value) : undefined)
        }
        isDocuSigned={isDocuSigned}
      />
      {coolingOffDetails && campaign?.state === CampaignDetailModelState.NSW && (
        <CoolingOffPeriodPanel
          key={`CoolingOffPanel${isPending ? 'Loaded' : ''}-${contractDate}`}
          contractDate={contractDate}
          coolingOffDetails={coolingOffDetails}
          onChange={(value) => editor.update('coolingOffDetails', value)}
          onDelete={() => editor.update('coolingOffDetails', undefined)}
        />
      )}
      {waiverOfCoolingOff && campaign?.state === CampaignDetailModelState.QLD && (
        <WaiverOfCoolingOffPanel
          key={`CoolingOffPanel${isPending ? 'Loaded' : ''}-${contractDate}`}
          contractDate={contractDate}
          waiverOfCoolingOff={waiverOfCoolingOff}
          onChange={(value) => editor.update('waiverOfCoolingOff', value)}
          onDelete={() => editor.update('waiverOfCoolingOff', undefined)}
        />
      )}

      {subjectToFinance?.condition &&
        subjectToFinance.condition !== Condition.NotSubjectToFinance && (
          <ConditionPanel
            id="subjectToFinance"
            key={`SubjectToFinancePanel${isPending ? 'Loaded' : ''}`}
            panelTitle="Finance"
            contractDate={contractDate}
            condition={subjectToFinance}
            onChange={(condition) => {
              editor.update('subjectToFinance', condition as FormCondition | undefined);
            }}
            onDelete={() => editor.update('subjectToFinance', undefined)}
            specialConditionIdToClauseNumberMap={specialConditionIdToClauseNumberMap ?? {}}
          />
        )}

      {buildingAndPestCondition?.condition &&
        buildingAndPestCondition.condition !== Condition.NotApplicable && (
          <ConditionPanel
            id="buildingAndPestCondition"
            key={`BuildingAndPestConditionPanel${isPending ? 'Loaded' : ''}`}
            panelTitle="Building & Pest"
            contractDate={contractDate}
            condition={buildingAndPestCondition}
            onChange={(condition) => {
              editor.update('buildingAndPestCondition', condition as FormCondition | undefined);
            }}
            onDelete={() => editor.update('buildingAndPestCondition', undefined)}
            specialConditionIdToClauseNumberMap={specialConditionIdToClauseNumberMap ?? {}}
          />
        )}

      {bodyCorporateRecordsCondition?.condition &&
        bodyCorporateRecordsCondition.condition !== Condition.NotApplicable && (
          <ConditionPanel
            id="bodyCorporateRecordsCondition"
            key={`BodyCorporateRecordsConditionPanel${isPending ? 'Loaded' : ''}`}
            panelTitle="Body Corporate Records"
            contractDate={contractDate}
            condition={bodyCorporateRecordsCondition}
            onChange={(condition) => {
              editor.update(
                'bodyCorporateRecordsCondition',
                condition as FormCondition | undefined
              );
            }}
            onDelete={() => editor.update('bodyCorporateRecordsCondition', undefined)}
            specialConditionIdToClauseNumberMap={specialConditionIdToClauseNumberMap ?? {}}
          />
        )}

      {!isPending && (
        <AgxRow gap="spacing-md" justifyContent="left" alignItems="flex-start">
          {!coolingOffDetails && campaign?.state === CampaignDetailModelState.NSW && (
            <AddCoolingOffPeriod
              id="add-cooling-off"
              onConfirm={(value) => editor.update('coolingOffDetails', value)}
            />
          )}
          {!waiverOfCoolingOff && campaign?.state === CampaignDetailModelState.QLD && (
            <AddWaiverOfCoolingOff
              id="add-cooling-off"
              onConfirm={(value) => editor.update('waiverOfCoolingOff', value)}
            />
          )}
          {(!subjectToFinance?.condition ||
            subjectToFinance.condition === Condition.NotSubjectToFinance) && (
            <AddSubjectToFinance
              id="add-finance"
              onConfirm={(value) => editor.update('subjectToFinance', value)}
            />
          )}
          {showNonNswAddConditionButtons &&
            (!buildingAndPestCondition?.condition ||
              buildingAndPestCondition.condition === Condition.NotApplicable) && (
              <AddBuildingAndPest
                id="add-building-and-pest"
                onConfirm={(value) => editor.update('buildingAndPestCondition', value)}
              />
            )}
          {showNonNswAddConditionButtons &&
            (!bodyCorporateRecordsCondition?.condition ||
              bodyCorporateRecordsCondition.condition === Condition.NotApplicable) && (
              <AddBodyCorporateRecords
                id="add-body-corporate-records"
                onConfirm={(value) => editor.update('bodyCorporateRecordsCondition', value)}
              />
            )}
        </AgxRow>
      )}

      {editor.confirmation && (
        <LinkedDateWarningModal
          impactedDates={editor.confirmation.impactedDates}
          sourceDateTypes={editor.confirmation.sourceDateTypes}
          onCancel={editor.cancelSave}
          onConfirm={editor.confirmSave}
        />
      )}
      {rexDisconnectedModal}
    </ContractInformationPage>
  );
};

export default ContractDates;
